import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

export type StandardItemCatalogRow = {
  standardItemId: string;
  requestCategory: string | null;
  mainGroup: string | null;
  group: string | null;
  category: string | null;
  subcategory: string | null;
  itemFamily: string | null;
  variant: string | null;
  nomenclature: string | null;
  uom: string | null;
  suggestedHsn4: string | null;
};

export type CatalogFilters = {
  requestCategory?: string;
  mainGroup?: string;
  group?: string;
  category?: string;
  subcategory?: string;
};

const LEVEL_KEYS = [
  "requestCategory",
  "mainGroup",
  "group",
  "category",
  "subcategory",
] as const;

export type CatalogLevelKey = (typeof LEVEL_KEYS)[number];

function appendFilters(u: URLSearchParams, f: CatalogFilters) {
  if (f.requestCategory) u.set("requestCategory", f.requestCategory);
  if (f.mainGroup) u.set("mainGroup", f.mainGroup);
  if (f.group) u.set("group", f.group);
  if (f.category) u.set("category", f.category);
  if (f.subcategory) u.set("subcategory", f.subcategory);
}

function toQuery(f: CatalogFilters): string {
  const u = new URLSearchParams();
  appendFilters(u, f);
  const s = u.toString();
  return s ? `?${s}` : "";
}

export async function fetchStandardItemDistinct(
  level: CatalogLevelKey,
  filters: CatalogFilters,
): Promise<string[]> {
  const base = `${getApiBaseUrl()}/api/standard-item-master/distinct/${encodeURIComponent(level)}`;
  const res = await apiFetch(`${base}${toQuery(filters)}`);
  if (!res.ok) throw new Error(await parseError(res));
  const j = (await res.json()) as { values?: string[] };
  return j.values ?? [];
}

export async function fetchStandardItemCatalogPage(input: {
  filters: CatalogFilters;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: StandardItemCatalogRow[]; total: number; limit: number; offset: number }> {
  const u = new URLSearchParams();
  appendFilters(u, input.filters);
  if (input.q?.trim()) u.set("q", input.q.trim());
  u.set("limit", String(input.limit ?? 80));
  u.set("offset", String(input.offset ?? 0));
  const res = await apiFetch(`${getApiBaseUrl()}/api/standard-item-master/items?${u}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    items: StandardItemCatalogRow[];
    total: number;
    limit: number;
    offset: number;
  }>;
}

export async function checkStandardItemCatalogAvailable(): Promise<boolean> {
  try {
    const res = await apiFetch(`${getApiBaseUrl()}/api/standard-item-master/health`);
    if (!res.ok) return false;
    const j = (await res.json()) as { ok?: boolean };
    return Boolean(j.ok);
  } catch {
    return false;
  }
}
